import { DataObjectBase, DataProperty } from '../../../../model/data';
import React, { ReactElement, useState } from 'react';
import { Form } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { useSavePropertyController } from '../../../use-save-property-controller';
import { EnumInput } from '../Inputs/enum-input';
import { hasToString } from '../has-to-string';
import { useSaveAllControllerContext } from '../../../use-save-all-controller';
import styles from './property-component.module.sass';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';

export interface PropTypes<T> {
    route: DataObjectBase[]
    dataObject: DataProperty<T>
    readonly: boolean
    parseValue: (value: string) => T
    isValid: (value: T) => boolean
    formComponent: (value: T, handleOnChange: (value: string) => void, disabled: boolean) => ReactElement;
    formatValue?: (value: any) => any;
    displayLabel?: boolean,
    labelDecorator?: (label: string) => string
}

export const PropertyComponent = <T extends undefined | hasToString, >({
                                                                           parseValue,
                                                                           isValid,
                                                                           route,
                                                                           dataObject,
                                                                           readonly,
                                                                           formComponent,
                                                                           formatValue = (value: T | null) => value?.toString(),
                                                                           displayLabel,
                                                                           labelDecorator = (label: string) => label
                                                                       }: PropTypes<T>) => {

    const [value, setValue] = useState<T>(dataObject.value);
    const [valid, setValid] = useState<boolean>(true);
    const [originalValue, setOriginalValue] = useState<T | null>(null);
    const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
    const controller = useSavePropertyController();
    const saveAllController = useSaveAllControllerContext();
    const [savingProperty, setSavingProperty] = useState<string | null>(null);

    const handleOnChange = (newValue: string) => {
        setUnsavedChanges(true);

        if (originalValue == null)
            setOriginalValue(value);

        const parsedValue = parseValue(newValue);
        const propertyIsValid = isValid(parsedValue);

        setValid(propertyIsValid);
        setValue(parsedValue);

        const propertyRoute = [...route, dataObject];
        if (propertyIsValid)
            saveAllController?.addUpdatedProperty(propertyRoute, parsedValue, formatValue(parsedValue));
        else
            saveAllController?.removeUpdatedProperty(propertyRoute);
    };

    const handleSave = async (propertyName: string) => {

        setSavingProperty(propertyName);

        const saved = await controller.handleSaveProperty({
            route: [...route, dataObject],
            value,
            formattedValue: formatValue(value)
        });

        if (saved) {
            setOriginalValue(null);
            setUnsavedChanges(false);

            saveAllController?.removeUpdatedProperty([...route, dataObject]);
        }

        setSavingProperty(null);
    };

    const resetToOriginalValue = () => {
        if (originalValue !== null) {
            setValue(originalValue);
            setOriginalValue(null);
            setUnsavedChanges(false);
            saveAllController?.removeUpdatedProperty([...route, dataObject]);
        }
    };

    const disabled = !dataObject.isEditable || readonly;

    return (
        <Col xl={6} className={`${styles.propertyComponent} w-100`}>
            {displayLabel &&
                <Form.Label data-testid={`label${dataObject.id}`}>{labelDecorator(dataObject.label)}</Form.Label>}
            <div>
                {dataObject.enumValues && Object.keys(dataObject.enumValues).length > 0 ? (
                    <EnumInput
                        value={value != null ? value.toString() : undefined}
                        enumValues={dataObject.enumValues}
                        disabled={disabled}
                        handleOnChange={handleOnChange}
                        data-testid={`input${dataObject.id}`}
                    />
                ) : (
                    formComponent(value, handleOnChange, disabled)
                )}
            </div>
            {!disabled && (
                <>
                    <Button onClick={() => handleSave(dataObject.label)} disabled={!valid || !unsavedChanges}
                            className="property-save-button" data-testid={`btnSave${dataObject.id}`}>
                        {savingProperty == dataObject.label ? (
                            <Spinner animation="border" size="sm" variant="light"
                                     style={{ height: '20px', width: '20px' }} />
                        ) : (
                            <i className="fas fa-save icon-with-text" />
                        )}
                    </Button>
                    {/*{originalValue != null &&*/}
                    <Button variant="light" className="property-reset-button" onClick={resetToOriginalValue}
                            disabled={!valid || !unsavedChanges} data-testid={`btnReset${dataObject.id}`}>
                        <i className="fas fa-undo-alt" />
                    </Button>
                    {/*}*/}
                </>
            )}
        </Col>
    );
};
